import * as React from 'react'
import { isEqual } from 'lodash'
import { STRAPI_USER_STORAGE_KEY } from '../../constants'
import { UsersPermissionsUser } from '../../generated/graphql'
import { useBrowserStorage } from '../../hooks'
import strapiHelpers, { StrapiLoginPayload } from '../../utils/strapiHelpers'
import { captureEvent, errorCode, identifyUser, resetAnalytics } from '../../lib/analytics'

type AuthProviderProps = {
  logout: () => void
  isAuthenticating: boolean
  isAuthenticated: boolean
  user: UsersPermissionsUser
  register: (username: string, email: string, password: string) => Promise<void>
  login: (identifier: string, password: string, rememberMe: boolean) => Promise<void>
  setUser: React.Dispatch<React.SetStateAction<UsersPermissionsUser | undefined>>
}

const AuthContext = React.createContext<Partial<AuthProviderProps>>({})

export const useAuthContext = () => React.useContext(AuthContext)

type UserStorage = StrapiLoginPayload

const AuthProvider: React.FC = ({ children }) => {
  const [localUser, setLocalUser, removeLocalUser] = useBrowserStorage<UserStorage>(
    STRAPI_USER_STORAGE_KEY,
    'local'
  )

  const [sessionUser, setSessionUser, removeSessionUser] = useBrowserStorage<UserStorage>(
    STRAPI_USER_STORAGE_KEY,
    'session'
  )

  const [isAuthenticated, setIsAuthenticated] = React.useState(false)
  const [isAuthenticating, setIsAuthenticating] = React.useState(true)

  const [user, setUser] = React.useState(sessionUser?.user || localUser?.user)

  const persistUser = (data: StrapiLoginPayload, rememberMe?: boolean) => {
    rememberMe ? setLocalUser(data) : setSessionUser(data)
    setUser(data?.user)
  }

  const logout = () => {
    removeLocalUser()
    removeSessionUser()
    setIsAuthenticated(false)
    setUser(undefined)
    resetAnalytics()
  }
  React.useEffect(() => {
    if (user?.confirmed) {
      setIsAuthenticated(true)
      if (localUser?.user && !isEqual(localUser?.user, user)) {
        setLocalUser({ jwt: localUser.jwt, user })
      }
      if (sessionUser?.user && !isEqual(sessionUser?.user, user)) {
        setSessionUser({ jwt: sessionUser.jwt, user })
      }
    } else {
      setIsAuthenticated(false)
    }
    setIsAuthenticating(false)
    // eslint-disable-next-line
  }, [user])

  // Identify by user id only — never phone number, email or name.
  const userId = (user as { id?: string | number } | undefined)?.id
  React.useEffect(() => {
    if (isAuthenticated && userId) identifyUser(userId)
  }, [isAuthenticated, userId])

  const login = async (email: string, password: string, rememberMe: boolean) => {
    try {
      const { data } = await strapiHelpers.login(email, password)
      identifyUser((data?.user as { id?: string | number } | undefined)?.id)
      captureEvent('dde_login_succeeded', { remember_me: Boolean(rememberMe) })
      persistUser(data, rememberMe)
    } catch (error) {
      captureEvent('dde_login_failed', { error_code: errorCode(error) })
      throw error
    }
  }

  const register = async (username: string, email: string, password: string) => {
    try {
      const { data } = await strapiHelpers.register(username, email, password)
      persistUser(data)
    } catch (error) {
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{ login, register, logout, isAuthenticated, isAuthenticating, user, setUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
